function escapeRegExp(text) {
	return String(text || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function normalizeToken(value) {
	if (value === null || value === undefined) return "";
	return String(value)
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.trim()
		.toLowerCase()
		.replace(/\s+/g, "_")
		.replace(/[^a-z0-9_]/g, "");
}

function replaceRefsAndEvaluate(formula, valuesByRefToken, valuesByRefName) {
	if (!formula) return null;
	let expr = String(formula).trim();
	if (expr.startsWith("=")) expr = expr.slice(1);

	if (valuesByRefName && typeof valuesByRefName === "object") {
		for (const [rawName, value] of Object.entries(valuesByRefName)) {
			if (!rawName) continue;
			if (!expr.includes(rawName)) continue;
			if (value === undefined || value === null) return null;
			expr = expr.replace(
				new RegExp(escapeRegExp(rawName), "g"),
				String(value),
			);
		}
	}

	const tokenMatches = expr.match(/[A-Za-z_][A-Za-z0-9_]*/g) || [];
	const uniqueTokens = [...new Set(tokenMatches)];

	for (const token of uniqueTokens) {
		const normalized = normalizeToken(token);
		const value = valuesByRefToken ? valuesByRefToken[normalized] : undefined;
		if (value === undefined || value === null || !isFinite(Number(value))) {
			return null;
		}
	}

	for (const token of uniqueTokens) {
		const normalized = normalizeToken(token);
		const value = valuesByRefToken[normalized];
		const re = new RegExp(`\\b${escapeRegExp(token)}\\b`, "g");
		expr = expr.replace(re, String(value));
	}

	if (!/^[0-9+\-*/().\s]*$/.test(expr)) {
		return null;
	}

	try {
		const result = Function(`"use strict"; return (${expr});`)();
		if (!isFinite(Number(result))) return null;
		return Number(result);
	} catch (e) {
		return null;
	}
}

function monthsWithNumericValue(monthMap) {
	return Object.keys(monthMap || {})
		.map((month) => Number(month))
		.filter((month) => {
			const value = monthMap[month];
			return (
				value !== null &&
				value !== undefined &&
				value !== "" &&
				isFinite(Number(value))
			);
		});
}

function lastMonthFromReference(config, valuesByConfigId) {
	if (!config?.totalizator_ref) return null;
	const refValues = valuesByConfigId[String(config.totalizator_ref)] || {};
	const months = monthsWithNumericValue(refValues);
	return months.length > 0 ? Math.max(...months) : 0;
}

function aggregateUpTo(config, valuesByConfigId, maxMonth) {
	const monthValues = valuesByConfigId[String(config.id)] || {};
	let total = 0;
	let count = 0;

	Object.entries(monthValues).forEach(([month, rawValue]) => {
		if (Number(month) > maxMonth) return;
		const value = Number(rawValue);
		if (!isFinite(value)) return;
		total += value;
		count += 1;
	});

	const kind = String(config.totalizator || "").trim().toLowerCase();
	if (kind === "average") return count > 0 ? total / count : 0;
	return total;
}

/**
 * Calcula totais de variáveis V2 (sum / average / calc), com suporte a totalizator_ref.
 * No calc, a coluna de referência limita soma e média usadas na fórmula ao último mês com valor.
 * @param {Array} configs - goals_variables_config (com GoalsVariable quando importada)
 * @param {Object} valuesByConfigId - { [configId]: { [month]: number } }
 */
export function computeVariablesTotals(configs = [], valuesByConfigId = {}) {
	const totals = {};
	const list = Array.isArray(configs) ? configs : [];

	const getConfigName = (config) =>
		config?.GoalsVariable?.name || config?.name || "";

	// 1ª passada: sum / average
	for (const config of list) {
		const totalizator = String(config.totalizator || "").trim().toLowerCase();
		if (totalizator !== "sum" && totalizator !== "average") continue;

		const referencedMonth = lastMonthFromReference(config, valuesByConfigId);
		const lastMonthWithValue =
			referencedMonth === null ? 12 : referencedMonth;

		const monthValues = valuesByConfigId[String(config.id)] || {};
		let total = 0;
		let count = 0;

		Object.entries(monthValues).forEach(([month, rawValue]) => {
			if (Number(month) > lastMonthWithValue) return;
			const value = Number(rawValue);
			if (!isFinite(value)) return;
			total += value;
			count += 1;
		});

		if (totalizator === "sum") {
			totals[config.id] = total;
		} else {
			totals[config.id] = count > 0 ? total / count : 0;
		}
	}

	// 2ª passada: calc.
	// Sem referência, reaplica a fórmula nos totais já calculados.
	// Com referência, soma/média das variáveis da fórmula só até o último mês dessa referência.
	for (const config of list) {
		const totalizator = String(config.totalizator || "").trim().toLowerCase();
		if (totalizator !== "calc" || !config.formula) continue;

		const maxMonth = lastMonthFromReference(config, valuesByConfigId);
		const valuesByRefName = {};
		const valuesByRefToken = {};

		for (const other of list) {
			const rawName = getConfigName(other);
			if (!rawName) continue;

			const otherKind = String(other.totalizator || "").trim().toLowerCase();
			let value;

			if (
				maxMonth !== null &&
				(otherKind === "sum" || otherKind === "average")
			) {
				value = aggregateUpTo(other, valuesByConfigId, maxMonth);
			} else if (totals[other.id] !== undefined) {
				value = totals[other.id];
			} else {
				continue;
			}

			valuesByRefName[rawName] = value;
			const token = normalizeToken(rawName);
			if (token) valuesByRefToken[token] = value;
		}

		const computed = replaceRefsAndEvaluate(
			config.formula,
			valuesByRefToken,
			valuesByRefName,
		);
		if (computed !== null) {
			totals[config.id] = computed;
		}
	}

	return totals;
}
